import React, { useEffect, useRef, useState } from 'react';
import type { FmMediaAsset } from '@/shared/config/mediaRegistry';

interface FmAssetImageRendererProps {
  asset: FmMediaAsset;
  alt: string;
  onLoad?: () => void;
  onError?: () => void;
}

export const FmAssetImageRenderer: React.FC<FmAssetImageRendererProps> = ({
  asset,
  alt,
  onLoad,
  onError,
}) => {
  const [displayedAsset, setDisplayedAsset] = useState<FmMediaAsset | null>(null);
  const [failed, setFailed] = useState(false);
  const deliveredRef = useRef<string | null>(null);
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  const assetRef = useRef(asset);
  onLoadRef.current = onLoad;
  onErrorRef.current = onError;
  assetRef.current = asset;

  useEffect(() => {
    let active = true;
    setFailed(false);
    const requestedAsset = assetRef.current;
    const pending = new Image();
    if (requestedAsset.srcSet) pending.srcset = requestedAsset.srcSet;
    pending.src = requestedAsset.src;

    const showPending = () => {
      if (!active) return;
      setDisplayedAsset(requestedAsset);
      setFailed(false);
      if (deliveredRef.current !== requestedAsset.src) {
        deliveredRef.current = requestedAsset.src;
        onLoadRef.current?.();
      }
    };
    const rejectPending = () => {
      if (!active) return;
      setDisplayedAsset(null);
      setFailed(true);
      onErrorRef.current?.();
    };

    pending.onload = showPending;
    pending.onerror = rejectPending;
    if (pending.complete && pending.naturalWidth > 0) showPending();

    return () => {
      active = false;
      pending.onload = null;
      pending.onerror = null;
    };
  }, [asset.src, asset.srcSet]);

  const renderedAsset = displayedAsset ?? asset;
  const ready = displayedAsset !== null || failed;
  return (
    <>
      <span className="fm-asset-image__loading" aria-hidden="true" />
      <img
        src={renderedAsset.src}
        srcSet={renderedAsset.srcSet}
        alt={alt}
        className="fm-asset-image__img"
        data-ready={ready ? 'true' : 'false'}
        onError={() => {
          setDisplayedAsset(null);
          setFailed(true);
          onErrorRef.current?.();
        }}
      />
    </>
  );
};

FmAssetImageRenderer.displayName = 'FmAssetImageRenderer';
